import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import Card from '../../components/base/Card';

export default function Corsi() {
  // Update document head for SEO
  useEffect(() => {
    document.title = 'Corsi di Trading Professionali - Fortal Society | Formazione Strutturata';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Corsi di trading professionali di Fortal Society. Formazione strutturata dal principiante all\'avanzato: Trading Foundations, Advanced Strategies, Risk Management. Community live e supporto continuo.');
    }
    
    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'corsi trading, formazione trading, corso forex, trading foundations, advanced strategies, risk management, corsi online trading, Fortal Society corsi');
    }
    
    // Add canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = `${import.meta.env.VITE_SITE_URL || 'https://fortalsociety.com'}/corsi`;
    
    // Add Schema.org JSON-LD for courses page
    const existingScript = document.querySelector('script[data-schema="courses"]');
    if (existingScript) {
      existingScript.remove();
    }
    
    const schema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Corsi di Trading Professionali",
      "description": "Formazione strutturata e progressiva per sviluppare competenze solide nel trading",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://fortalsociety.com'}/corsi`,
      "mainEntity": [
        {
          "@type": "Course",
          "name": "Trading Foundations",
          "description": "Le basi solide per iniziare a fare trading con consapevolezza e disciplina",
          "provider": {
            "@type": "Organization",
            "name": "Fortal Society",
            "url": `${import.meta.env.VITE_SITE_URL || 'https://fortalsociety.com'}`
          },
          "courseMode": "online",
          "educationalLevel": "Principiante",
          "timeRequired": "P8W",
          "offers": {
            "@type": "Offer",
            "price": "497",
            "priceCurrency": "EUR",
            "availability": "https://schema.org/InStock"
          },
          "hasCourseInstance": {
            "@type": "CourseInstance",
            "courseMode": "online",
            "instructor": {
              "@type": "Person",
              "name": "Fortal Society Team"
            }
          }
        },
        {
          "@type": "Course",
          "name": "Advanced Strategies", 
          "description": "Strategie avanzate e tecniche professionali per trader esperti",
          "provider": {
            "@type": "Organization",
            "name": "Fortal Society",
            "url": `${import.meta.env.VITE_SITE_URL || 'https://fortalsociety.com'}`
          },
          "courseMode": "online",
          "educationalLevel": "Avanzato",
          "timeRequired": "P12W",
          "offers": {
            "@type": "Offer",
            "price": "897",
            "priceCurrency": "EUR",
            "availability": "https://schema.org/InStock"
          },
          "hasCourseInstance": {
            "@type": "CourseInstance",
            "courseMode": "online",
            "instructor": {
              "@type": "Person",
              "name": "Fortal Society Team"
            }
          }
        },
        {
          "@type": "Course",
          "name": "Risk Management Mastery",
          "description": "Padroneggia l'arte del risk management per proteggere il tuo capitale",
          "provider": {
            "@type": "Organization", 
            "name": "Fortal Society",
            "url": `${import.meta.env.VITE_SITE_URL || 'https://fortalsociety.com'}`
          },
          "courseMode": "online",
          "educationalLevel": "Intermedio",
          "timeRequired": "P6W",
          "offers": {
            "@type": "Offer",
            "price": "397",
            "priceCurrency": "EUR",
            "availability": "https://schema.org/InStock"
          },
          "hasCourseInstance": {
            "@type": "CourseInstance",
            "courseMode": "online",
            "instructor": {
              "@type": "Person",
              "name": "Fortal Society Team"
            }
          }
        }
      ],
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": `${import.meta.env.VITE_SITE_URL || 'https://fortalsociety.com'}/`
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Corsi",
            "item": `${import.meta.env.VITE_SITE_URL || 'https://fortalsociety.com'}/corsi`
          }
        ]
      }
    };
    
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.setAttribute('data-schema', 'courses');
    schemaScript.textContent = JSON.stringify(schema);
    document.head.appendChild(schemaScript);
    
    return () => {
      // Cleanup function to remove schema when component unmounts
      const script = document.querySelector('script[data-schema="courses"]');
      if (script) {
        script.remove();
      }
    };
  }, []);

  const courses = [
    {
      id: 'trading-foundations',
      title: 'Trading Foundations',
      level: 'Principiante',
      duration: '8 settimane',
      price: '€497',
      cover: 'https://public.readdy.ai/ai/img_res/640a969fda7ff57fc8ada49bab841aae.jpg',
      description: 'Le basi solide per iniziare a fare trading con consapevolezza e disciplina.',
      badge: 'Più Popolare'
    },
    {
      id: 'advanced-strategies',
      title: 'Advanced Strategies',
      level: 'Avanzato',
      duration: '12 settimane',
      price: '€897',
      cover: 'https://readdy.ai/api/search-image?query=Advanced%20trading%20strategies%20course%20design%2C%20complex%20financial%20analysis%20charts%2C%20professional%20trading%20workspace%2C%20multiple%20monitors%20with%20market%20data%2C%20sophisticated%20trading%20algorithms%20visualization%2C%20dark%20theme%20with%20golden%20accents&width=400&height=300&seq=course-advanced-gold&orientation=landscape',
      description: 'Strategie avanzate e tecniche professionali per trader esperti.',
      badge: 'Masterclass Live'
    },
    {
      id: 'risk-management',
      title: 'Risk Management Mastery',
      level: 'Intermedio',
      duration: '6 settimane',
      price: '€397',
      cover: 'https://readdy.ai/api/search-image?query=Risk%20management%20trading%20course%20design%2C%20financial%20risk%20assessment%20charts%2C%20portfolio%20protection%20visualization%2C%20professional%20trading%20dashboard%2C%20dark%20background%20with%20golden%20accents%2C%20security%20and%20protection%20theme&width=400&height=300&seq=course-risk-gold&orientation=landscape',
      description: 'Padroneggia l\'arte del risk management per proteggere il tuo capitale.',
      badge: 'Essenziale'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950 relative">
      <Navbar />
      
      {/* Contenuto bloccato con blur */}
      <div className="blur-sm pointer-events-none select-none">
        <div className="pt-20">
          {/* Header */}
          <header className="py-16 bg-slate-900/30">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 animate-slide-in-up">
                Corsi di Trading
              </h1>
              <p className="text-lg text-slate-300 max-w-3xl mx-auto animate-slide-in-up delay-200">
                Formazione strutturata e progressiva per sviluppare competenze solide nel trading. 
                Dalla teoria alla pratica, con supporto continuo della community.
              </p>
            </div>
          </header>

          {/* Courses Grid */}
          <main className="py-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {courses.map((course, index) => (
                  <Card key={course.id} variant="glass" className="overflow-hidden hover-lift animate-bounce-in glass-gradient glow-gold" style={{animationDelay: `${index * 0.2}s`}}>
                    <div className="relative">
                      <img 
                        src={course.cover}
                        alt={`Corso ${course.title} - ${course.description}`}
                        className="w-full h-48 object-cover rounded-xl mb-4 hover-glow"
                      />
                      {course.badge && (
                        <div className="absolute top-3 right-3 bg-gradient-to-r from-amber-400 to-yellow-500 text-slate-900 px-3 py-1 rounded-full text-xs font-bold animate-pulse-gold glow-gold">
                          {course.badge}
                        </div>
                      )}
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <h3 className="text-xl font-bold text-white mb-2 animate-shimmer">{course.title}</h3>
                        <p className="text-slate-300 text-sm">{course.description}</p>
                      </div>
                      
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-slate-400">Livello: <strong className="text-amber-400">{course.level}</strong></span>
                        <span className="text-slate-400">Durata: <strong className="text-white">{course.duration}</strong></span>
                      </div>
                      
                      <div className="flex justify-between items-center pt-4 border-t border-amber-200/20">
                        <div className="text-2xl font-bold text-white animate-pulse-gold">{course.price}</div>
                        <Button variant="primary" size="sm" href={`/corsi/${course.id}`} className="hover-lift">
                          Scopri di Più
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </main>

          {/* Why Choose Our Courses */}
          <section className="py-16 bg-slate-900/30">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12 animate-slide-in-up">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                  Perché Scegliere i Nostri Corsi
                </h2>
                <p className="text-lg text-slate-300 max-w-3xl mx-auto">
                  Formazione pratica basata su esperienza reale e risultati verificabili.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                <div className="text-center animate-scale-in">
                  <div className="w-16 h-16 bg-gradient-to-br from-amber-400/20 to-yellow-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4 animate-float glow-gold">
                    <div className="w-8 h-8 flex items-center justify-center">
                      <i className="ri-live-line text-2xl text-amber-400"></i>
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">Live Sessions</h3>
                  <p className="text-slate-400 text-sm">Sessioni dal vivo con Q&A interattive</p>
                </div>
                
                <div className="text-center animate-scale-in delay-100">
                  <div className="w-16 h-16 bg-gradient-to-br from-amber-400/20 to-yellow-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4 animate-float delay-100 glow-gold">
                    <div className="w-8 h-8 flex items-center justify-center">
                      <i className="ri-community-line text-2xl text-amber-400"></i>
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">Community</h3>
                  <p className="text-slate-400 text-sm">Accesso esclusivo alla community Telegram</p>
                </div>
                
                <div className="text-center animate-scale-in delay-200">
                  <div className="w-16 h-16 bg-gradient-to-br from-amber-400/20 to-yellow-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4 animate-float delay-200 glow-gold">
                    <div className="w-8 h-8 flex items-center justify-center">
                      <i className="ri-file-text-line text-2xl text-amber-400"></i>
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">Materiali</h3>
                  <p className="text-slate-400 text-sm">Template, checklist e strumenti pratici</p>
                </div>
                
                <div className="text-center animate-scale-in delay-300">
                  <div className="w-16 h-16 bg-gradient-to-br from-amber-400/20 to-yellow-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4 animate-float delay-300 glow-gold">
                    <div className="w-8 h-8 flex items-center justify-center">
                      <i className="ri-headphone-line text-2xl text-amber-400"></i>
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">Supporto</h3>
                  <p className="text-slate-400 text-sm">Supporto continuo durante tutto il percorso</p>
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-16">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <Card variant="gradient" className="glass-gradient glow-gold-strong animate-bounce-in">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 animate-shimmer">
                  Inizia il Tuo Percorso di Formazione
                </h2>
                <p className="text-lg text-slate-300 mb-8">
                  Non sai quale corso scegliere? Unisciti alla community e ricevi una consulenza gratuita 
                  per identificare il percorso più adatto alle tue esigenze.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button variant="primary" size="lg" className="hover-lift animate-pulse-gold">
                    <div className="w-5 h-5 flex items-center justify-center mr-2">
                      <i className="ri-telegram-line"></i>
                    </div>
                    Consulenza Gratuita
                  </Button>
                  <Button variant="secondary" size="lg" className="hover-lift">
                    <div className="w-5 h-5 flex items-center justify-center mr-2">
                      <i className="ri-calendar-line"></i>
                    </div>
                    Prenota una Call
                  </Button>
                </div>
              </Card>
            </div>
          </section>
        </div>

        <Footer />
      </div>

      {/* Overlay Coming Soon */}
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm">
        <div className="text-center px-4 animate-bounce-in">
          <div className="w-24 h-24 flex items-center justify-center mx-auto mb-8 bg-gradient-to-br from-amber-400/20 to-yellow-500/20 rounded-full animate-float glow-gold-strong">
            <i className="ri-lock-line text-6xl text-amber-400"></i>
          </div>
          <h2 className="text-5xl md:text-6xl font-bold text-gradient-gold mb-4 animate-pulse-gold glow-gold-strong">
            Coming Soon
          </h2>
          <p className="text-xl text-slate-300 mb-8 max-w-md mx-auto">
            Questa sezione sarà presto disponibile. Resta aggiornato!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="primary" size="lg" href="/" className="hover-lift animate-pulse-gold">
              <div className="w-5 h-5 flex items-center justify-center mr-2">
                <i className="ri-home-line"></i>
              </div>
              Torna alla Home
            </Button>
            <Button variant="secondary" size="lg" className="hover-lift">
              <div className="w-5 h-5 flex items-center justify-center mr-2">
                <i className="ri-telegram-line"></i>
              </div>
              Entra in Community
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
